<template>
    <div class="departmental-plan-management">
        <TargetPageHeader title="부서별 목표 설정 및 계획 관리" subtitle="조직별 이행계획을 관리하고 추적합니다.">
            <template #actions>
                <button type="button" class="dept-btn-add" @click="openAddModal">
                    <i class="fa fa-plus"></i> 부서별 이행계획 추가
                </button>
            </template>
        </TargetPageHeader>

        <div v-if="loading" class="loading-container">
            <div class="spinner"></div>
            <p>데이터를 불러오는 중...</p>
        </div>

        <div v-else-if="groupedOrgTargets.length === 0" class="dept-empty-state">
            <i class="fa fa-sitemap"></i>
            <p>등록된 부서별 이행계획이 없습니다.</p>
        </div>

        <div v-else class="dept-content-section">
            <div class="dept-table-wrapper">
                <div class="table-header-sticky">
                    <table class="departmental-table">
                        <colgroup>
                            <col class="col-org-name" />
                            <col class="col-assigned" />
                            <col class="col-achieved" />
                            <col class="col-progress" />
                            <col class="col-status" />
                            <col class="col-action" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>부서명 (조직 체계)</th>
                                <th>부서 할당량 (kgCO2e)</th>
                                <th>실제 배출량 (kgCO2e)</th>
                                <th>목표달성률(%)</th>
                                <th>상태</th>
                                <th></th>
                            </tr>
                        </thead>
                    </table>
                </div>

                <div v-for="group in groupedOrgTargets" :key="`org-boundary-${group.orgBoundaryIdx}`" class="org-group">
                    <div class="org-header" @click="toggleCollapse(group.orgBoundaryIdx)">
                        <div class="org-header-left">
                            <i
                                class="fa"
                                :class="collapsedOrgs[group.orgBoundaryIdx] ? 'fa-chevron-right' : 'fa-chevron-down'"
                            ></i>
                            <span class="org-name">{{ group.orgName }}</span>
                            <span class="org-code">({{ group.orgCode }})</span>
                            <span class="org-count">{{ group.targets.length }}건</span>
                        </div>
                    </div>

                    <div v-show="!collapsedOrgs[group.orgBoundaryIdx]" class="org-body">
                        <table class="org-table">
                            <colgroup>
                                <col class="col-org-name" />
                                <col class="col-assigned" />
                                <col class="col-achieved" />
                                <col class="col-progress" />
                                <col class="col-status" />
                                <col class="col-action" />
                            </colgroup>
                            <tbody>
                                <tr v-for="target in group.targets" :key="target.orgTargetIdx" class="target-row">
                                    <td class="pathway-cell">
                                        <div class="pathway-cell-wrapper">
                                            <div class="target-info">
                                                <span class="target-label"
                                                    >{{ target.pathway?.pathwayYear || "-" }}년 이행계획</span
                                                >
                                            </div>
                                        </div>
                                    </td>
                                    <td class="number-cell">
                                        {{ formatTargetCell(target.assignedAmount) }}
                                    </td>

                                    <td class="number-cell achieved">
                                        {{ formatTargetCell(target.actualEmissions) }}
                                    </td>
                                    <td class="progress-cell">
                                        <div class="progress-wrapper">
                                            <div class="progress-bar-container">
                                                <div
                                                    class="progress-bar-fill"
                                                    :style="{ width: Math.min(target.achieveRate || 0, 100) + '%' }"
                                                ></div>
                                            </div>
                                            <span class="progress-text">{{ target.achieveRate || 0 }}%</span>
                                        </div>
                                    </td>
                                    <td class="status-cell">
                                        <span :class="['status-badge', getStatusClass(target.goalStat)]">
                                            {{ getStatusLabel(target.goalStat) }}
                                        </span>
                                    </td>
                                    <td class="action-cell">
                                        <div class="action-buttons">
                                            <IconButton
                                                icon="fas fa-edit"
                                                title="수정"
                                                @click="openEditModal(target)"
                                            />
                                            <IconButton
                                                icon="fas fa-trash"
                                                variant="delete"
                                                title="삭제"
                                                @click="deletePlan(target)"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- 추가/수정 모달 -->
        <b-modal
            v-model="showModal"
            :title="isEditMode ? '부서별 계획 수정' : '부서별 계획 추가'"
            size="lg"
            @hidden="resetForm"
        >
            <div class="modal-form">
                <div class="form-row" v-if="!isEditMode">
                    <div class="form-group">
                        <label>이행계획(연도) <span class="required">*</span></label>
                        <select v-model.number="form.pathwayIdx" class="form-control">
                            <option :value="null">선택하세요</option>
                            <option v-for="p in pathwayList" :key="p.pathwayIdx" :value="p.pathwayIdx">
                                {{ getPathwayLabel(p) }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="form-row">
                    <div class="form-group">
                        <label>조직 경계 <span class="required">*</span></label>
                        <select v-model.number="form.orgBoundaryIdx" class="form-control" :disabled="isEditMode">
                            <option :value="null">선택하세요</option>
                            <option v-for="b in orgBoundaryList" :key="b.orgBoundaryIdx" :value="b.orgBoundaryIdx">
                                {{ b.orgBoundaryName || b.orgBoundaryIdx }} ({{ b._orgName || "-" }})
                            </option>
                        </select>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>할당된 목표 배출량 (kgCO2e) <span class="required">*</span></label>
                        <input
                            v-model.number="form.assignedAmount"
                            type="number"
                            class="form-control"
                            placeholder="예: 16024240"
                            step="1"
                            min="0"
                        />
                        <small class="form-unit-hint">연도별 이행계획 목표와 동일 단위(kg). 톤(t)은 ×1000</small>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>실제 배출량 (kgCO2e)</label>
                        <input
                            v-model.number="form.actualEmissions"
                            type="number"
                            class="form-control"
                            placeholder="미입력 시 달성률 미계산"
                            step="1"
                            min="0"
                        />
                        <small class="form-unit-hint">확정 인벤토리 연간 합(kg). 대시보드 실적은 인벤토리 기준</small>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>상태</label>
                        <select v-model="form.goalStat" class="form-control">
                            <option v-for="opt in goalStatOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>비고</label>
                        <textarea v-model="form.remarks" class="form-control" rows="3"></textarea>
                    </div>
                </div>
            </div>

            <template #modal-footer>
                <button class="btn btn-secondary" @click="showModal = false">취소</button>
                <button class="btn btn-primary" @click="savePlan" :disabled="saving">
                    <span v-if="saving"><i class="fa fa-spinner fa-spin"></i> 저장 중...</span>
                    <span v-else>저장</span>
                </button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import CarbonCodes from "../../../codes/carbonCodes";
import * as targetService from "../services/targetService";
import carbonApi from "../../../api";
import IconButton from "../../common/IconButton.vue";
import TargetPageHeader from "./TargetPageHeader.vue";
import { formatTargetEmissionValue } from "../targetUnits";

export default {
    name: "DepartmentalPlanManagement",
    components: { IconButton, TargetPageHeader },
    props: {
        isActive: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            orgTargets: [],
            pathwayList: [],
            organizationList: [],
            orgBoundaryList: [],
            loading: false,
            showModal: false,
            isEditMode: false,
            saving: false,
            collapsedOrgs: {},
            form: {
                orgTargetIdx: null,
                pathwayIdx: null,
                orgBoundaryIdx: null,
                assignedAmount: null,
                actualEmissions: null,
                goalStat: "DRAFT",
                remarks: "",
            },
        };
    },
    computed: {
        goalStatOptions() {
            return CarbonCodes.goalStat.getList();
        },
        groupedOrgTargets() {
            const groups = {};

            this.orgTargets.forEach((target) => {
                const orgBoundary = target.orgBoundary;
                // orgBoundaryName별 그룹핑: 동일 조직경계(orgBoundaryIdx)끼리 묶음
                const orgBoundaryIdx = orgBoundary?.orgBoundaryIdx ?? target.orgBoundaryIdx;
                const groupKey = orgBoundaryIdx;
                if (!groups[groupKey]) {
                    groups[groupKey] = {
                        orgBoundaryIdx: groupKey,
                        orgName:
                            orgBoundary?.orgBoundaryName ||
                            orgBoundary?.organization?.orgName ||
                            `조직경계 #${target.orgBoundaryIdx}`,
                        orgCode: orgBoundary?.organization?.orgCode || "-",
                        targets: [],
                    };
                }
                groups[groupKey].targets.push(target);
            });

            return Object.values(groups);
        },
    },
    watch: {
        isActive: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.initialize();
                }
            },
        },
    },
    methods: {
        async initialize() {
            await Promise.all([this.loadOrgTargets(), this.loadPathways(), this.loadOrganizations()]);
            await this.loadOrgBoundaries();
        },
        async loadOrgTargets() {
            this.loading = true;
            try {
                this.orgTargets = await targetService.fetchDepartmentalPlans();
            } catch (error) {
                console.error("부서별 이행계획 목록 조회 실패:", error);
                this.$bvToast.toast("부서별 이행계획 목록을 불러오는데 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.loading = false;
            }
        },
        async loadPathways() {
            try {
                // 전체 pathway 목록(연도별 이행계획) 조회 - 추가 모달용
                const response = await carbonApi.annualPlan.getAnnualPlans(null, {});
                const payload = response?.data ?? response;
                const data = payload?.data ?? payload;
                if (Array.isArray(data?.pathways)) {
                    this.pathwayList = data.pathways;
                } else if (Array.isArray(data?.items)) {
                    this.pathwayList = data.items;
                } else if (Array.isArray(data)) {
                    this.pathwayList = data;
                } else {
                    this.pathwayList = [];
                }
                if (this.pathwayList.length === 0) {
                    const goals = await carbonApi.target.getTargets();
                    if (goals?.data?.targets?.length > 0) {
                        const res = await carbonApi.annualPlan.getAnnualPlans(goals.data.targets[0].targetIdx);
                        this.pathwayList = res?.data?.pathways || [];
                    }
                }
            } catch (error) {
                console.error("연도별 이행계획 목록 조회 실패:", error);
                this.pathwayList = [];
            }
        },
        async loadOrganizations() {
            try {
                const response = await carbonApi.organization.getOrgs();

                // 응답 구조: { success: true, data: { organizations: [...] } }
                const responseData = response?.data ?? response;
                const data = responseData?.data ?? responseData;

                if (data?.organizations) {
                    this.organizationList = data.organizations;
                } else if (Array.isArray(data)) {
                    this.organizationList = data;
                } else if (Array.isArray(responseData)) {
                    this.organizationList = responseData;
                } else {
                    this.organizationList = [];
                }
            } catch (error) {
                console.error("조직 목록 조회 실패:", error);
                this.$bvToast.toast("조직 목록을 불러오는데 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            }
        },
        async loadOrgBoundaries() {
            try {
                if (!this.organizationList || this.organizationList.length === 0) {
                    this.orgBoundaryList = [];
                    return;
                }
                const allBoundaries = [];
                for (const org of this.organizationList) {
                    const orgCode = org.orgCode ?? org.orgIdx;
                    if (!orgCode) continue;
                    try {
                        const boundaries = await targetService.fetchOrgBoundaries(orgCode, "ACTIVE");
                        const withOrg = (boundaries || []).map((b) => ({
                            ...b,
                            _orgName: org.orgName,
                            _orgCode: org.orgCode,
                        }));
                        allBoundaries.push(...withOrg);
                    } catch (err) {
                        console.warn(`조직 경계 조회 실패 (orgCode=${orgCode}):`, err?.message);
                    }
                }
                this.orgBoundaryList = allBoundaries;
            } catch (error) {
                console.error("조직 경계 목록 조회 실패:", error);
                this.orgBoundaryList = [];
            }
        },
        toggleCollapse(orgBoundaryIdx) {
            this.$set(this.collapsedOrgs, orgBoundaryIdx, !this.collapsedOrgs[orgBoundaryIdx]);
        },
        async openAddModal() {
            this.isEditMode = false;
            await Promise.all([this.loadOrganizations(), this.loadPathways()]);
            await this.loadOrgBoundaries();
            this.resetForm();
            this.showModal = true;
        },
        async openEditModal(target) {
            this.isEditMode = true;
            await Promise.all([this.loadOrganizations(), this.loadPathways()]);
            await this.loadOrgBoundaries();
            this.form = {
                orgTargetIdx: target.orgTargetIdx,
                pathwayIdx: target.pathwayIdx,
                orgBoundaryIdx: target.orgBoundaryIdx,
                assignedAmount: target.assignedAmount,
                actualEmissions: target.actualEmissions,
                goalStat: target.goalStat,
                remarks: target.remarks,
            };
            this.showModal = true;
        },
        async savePlan() {
            if (!this.validateForm()) {
                return;
            }

            this.saving = true;
            try {
                if (this.isEditMode) {
                    const payload = {
                        assignedAmount: this.form.assignedAmount,
                        actualEmissions: this.form.actualEmissions,
                        goalStat: this.form.goalStat,
                        remarks: this.form.remarks,
                    };
                    await targetService.updateDepartmentalPlan(this.form.pathwayIdx, this.form.orgTargetIdx, payload);
                    this.$bvToast.toast("부서별 계획이 수정되었습니다.", {
                        variant: "success",
                        title: "저장 완료",
                    });
                } else {
                    const payload = {
                        pathwayIdx: this.form.pathwayIdx,
                        orgBoundaryIdx: this.form.orgBoundaryIdx,
                        assignedAmount: this.form.assignedAmount,
                        actualEmissions: this.form.actualEmissions ?? null,
                        goalStat: this.form.goalStat,
                        remarks: this.form.remarks || null,
                    };
                    await targetService.createDepartmentalPlan(payload);
                    this.$bvToast.toast("부서별 계획이 추가되었습니다.", {
                        variant: "success",
                        title: "저장 완료",
                    });
                }

                this.showModal = false;
                await this.loadOrgTargets();
            } catch (error) {
                console.error("부서별 계획 저장 실패:", error);
                this.$bvToast.toast(error.message || "부서별 계획 저장에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.saving = false;
            }
        },
        async deletePlan(target) {
            const displayName =
                target.orgBoundary?.organization?.orgName ||
                target.orgBoundary?.orgBoundaryName ||
                `조직경계 #${target.orgBoundaryIdx}`;
            const confirmed = await this.alertConfirmWarning(`"${displayName}" 부서의 이행계획을 삭제하시겠습니까?`);

            if (!confirmed.value) return;

            try {
                await targetService.deleteDepartmentalPlan(target.pathwayIdx, target.orgTargetIdx);
                this.$bvToast.toast("부서별 계획이 삭제되었습니다.", {
                    variant: "success",
                    title: "삭제 완료",
                });
                await this.loadOrgTargets();
            } catch (error) {
                console.error("부서별 계획 삭제 실패:", error);
                this.$bvToast.toast(error.message || "부서별 계획 삭제에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            }
        },
        validateForm() {
            if (!this.isEditMode) {
                if (!this.form.pathwayIdx) {
                    this.$bvToast.toast("이행계획(연도)을 선택하세요.", { variant: "warning", title: "입력 오류" });
                    return false;
                }
                if (!this.form.orgBoundaryIdx) {
                    this.$bvToast.toast("조직 경계를 선택하세요.", { variant: "warning", title: "입력 오류" });
                    return false;
                }
            }
            if (
                this.form.assignedAmount === null ||
                this.form.assignedAmount === undefined ||
                this.form.assignedAmount === ""
            ) {
                this.$bvToast.toast("할당 배출량을 입력하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            return true;
        },
        resetForm() {
            this.form = {
                orgTargetIdx: null,
                pathwayIdx: null,
                orgBoundaryIdx: null,
                assignedAmount: null,
                actualEmissions: null,
                goalStat: "DRAFT",
                remarks: "",
            };
        },
        getStatusLabel(status) {
            return CarbonCodes.goalStat.getLabel(status) || status;
        },
        getStatusClass(status) {
            return `status-${status}`;
        },
        getPathwayLabel(pathway) {
            if (!pathway) return "-";

            const pathwayIdx = pathway.pathwayIdx != null ? pathway.pathwayIdx : "";
            const year = pathway.pathwayYear || "-";
            const goalName = pathway.emissionTarget?.goalName || pathway.target?.goalName || null;
            const targetEmissions = pathway.targetEmissions;

            const parts = [`${year}년`];

            if (goalName) {
                parts.push(goalName);
            }

            if (targetEmissions != null && targetEmissions !== "") {
                parts.push(formatTargetEmissionValue(targetEmissions, this.formatNumber));
            }

            if (pathwayIdx !== "") {
                parts.push(`(이행계획 ID : ${pathwayIdx})`);
            }

            return parts.join(" · ");
        },
        formatNumber(num) {
            if (!num && num !== 0) return "-";
            return Number(num).toLocaleString("ko-KR", { maximumFractionDigits: 3 });
        },
        formatTargetCell(value) {
            return formatTargetEmissionValue(value, this.formatNumber);
        },
    },
};
</script>

<style scoped>
.departmental-plan-management {
    padding: 16px;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.dept-btn-add {
    padding: 8px 15px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    background: #52b788;
    color: white;
}

.dept-btn-add:hover {
    background: #40916c;
    transform: translateY(-1px);
}

.dept-btn-add i {
    margin-right: 6px;
}

.loading-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #6b7280;
}

.loading-container p {
    margin-top: 12px;
    font-size: 14px;
}

.dept-empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
}

.dept-empty-state i {
    font-size: 48px;
    margin-bottom: 12px;
    color: #d1d5db;
}

.dept-empty-state p {
    font-size: 14px;
}

.dept-content-section {
    flex: 1;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.dept-table-wrapper {
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
    min-height: 0;
    position: relative;
    max-height: 510px;
}

.table-header-sticky {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #f9fafb;
}

.departmental-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
}

.departmental-table thead {
    background: #f9fafb;
}

.departmental-table thead th {
    background: #f9fafb;
}

.departmental-table th {
    padding: 10px 0;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
    line-height: 1.4;
    white-space: nowrap;
    text-align: center;
}

/* 열 너비 설정 */
.departmental-table col.col-org-name,
.org-table col.col-org-name {
    width: 20%;
}

.departmental-table col.col-assigned,
.org-table col.col-assigned {
    width: 15%;
}

.departmental-table col.col-achieved,
.org-table col.col-achieved {
    width: 15%;
}

.departmental-table col.col-progress,
.org-table col.col-progress {
    width: 15%;
}

.departmental-table col.col-status,
.org-table col.col-status {
    width: 10%;
}

.departmental-table col.col-action,
.org-table col.col-action {
    width: 10%;
}

.org-group {
    border-bottom: 1px solid #e5e7eb;
}

.org-group:last-child {
    border-bottom: none;
}

.org-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    background: #f9fafb;
    cursor: pointer;
    transition: background 0.2s;
    border-bottom: 1px solid #e5e7eb;
}

.org-header:hover {
    background: #f3f4f6;
}

.org-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.org-header-left i {
    font-size: 11px;
    color: #6b7280;
    width: 12px;
}

.org-name {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
}

.org-code {
    font-size: 12px;
    color: #6b7280;
}

.org-count {
    font-size: 11px;
    color: #6b7280;
    padding: 2px 6px;
    background: #e5e7eb;
    border-radius: 10px;
}

.org-total {
    font-size: 12px;
    font-weight: 600;
    color: #10b981;
}

.org-body {
    background: white;
}

.org-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
}

.target-row {
    transition: background 0.15s;
}

.target-row:hover {
    background: #f9fafb;
}

.target-row td {
    padding: 8px 0;
    font-size: 12px;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
    text-align: center;
}

.target-row:last-child td {
    border-bottom: none;
}

.pathway-cell {
    position: relative;
}

.pathway-cell-wrapper {
    display: flex;
    align-items: center;
    min-height: 100%;
    margin-left: 33px;
}

.target-info {
    display: flex;
    flex-direction: column;
    padding-left: 20px;
    border-left: 2px solid #d1d5db;
}

.target-label {
    font-size: 12px;
    color: #374151;
}

.number-cell {
    font-weight: 500;
    color: #374151;
    white-space: nowrap;
}

.number-cell.achieved {
    color: #10b981;
}

.period-cell {
    text-align: center;
    color: #374151;
    white-space: nowrap;
}

.progress-cell {
    padding: 8px 0;
}

.progress-wrapper {
    display: flex;
    align-items: center;
}

.progress-bar-container {
    flex: 1;
    height: 18px;
    background: #f3f4f6;
    border-radius: 4px;
    position: relative;
}

.progress-bar-fill {
    height: 100%;
    background: #10b981;
    border-radius: 4px;
    transition: width 0.3s;
}

.progress-text {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    min-width: 40px;
    text-align: right;
    margin-left: 12px;
}

.status-cell {
    text-align: center;
}

.status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
}

.status-draft {
    background: #f3f4f6;
    color: #6b7280;
}

.status-active {
    background: #dbeafe;
    color: #1e40af;
}

.status-achieved {
    background: #d1fae5;
    color: #065f46;
}

.status-failed {
    background: #fee2e2;
    color: #991b1b;
}

.status-abandoned {
    background: #f3f4f6;
    color: #6b7280;
}

.action-cell {
    text-align: center;
}

.action-buttons {
    display: flex;
    gap: 8px;
    justify-content: center;
}

.btn-action {
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 0;
    font-size: 13px;
    cursor: pointer;
    transition: opacity 0.15s;
    background: transparent;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-action:hover {
    opacity: 0.7;
    color: #374151;
}

.btn-action.btn-edit:hover {
    color: #047857;
}

.btn-action.btn-delete:hover {
    color: #ef4444;
}

.spinner {
    display: inline-block;
    width: 24px;
    height: 24px;
    border: 3px solid #f3f4f6;
    border-top-color: #10b981;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* 모달 폼 스타일 */
.modal-form {
    padding: 8px 0;
}

.form-row {
    display: flex;
    gap: 12px;
}

.form-group {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.form-group label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
}

.form-unit-hint {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: #6b7280;
    line-height: 1.4;
}

.required {
    color: #ef4444;
}

.form-control {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 13px;
    transition: border-color 0.2s;
}

.form-control:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
}

.form-control:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
}

.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary {
    background: #f3f4f6;
    color: #4b5563;
}

.btn-secondary:hover {
    background: #e5e7eb;
}

.btn-primary {
    background: #10b981;
    color: white;
}

.btn-primary:hover {
    background: #059669;
}

.btn-primary:disabled {
    background: #9ca3af;
    cursor: not-allowed;
}
</style>
